import { useEffect, useRef, useState } from 'react';

interface ResultScreenProps {
  score: number;
  totalQuestions: number;
  correctCount: number;
  onRetry: () => void;
  onNext: () => void;
}

function getComment(score: number): string {
  if (score >= 90) return '기호와 숫자를 아주 빠르게 짝지었어요.';
  if (score >= 70) return '기호와 숫자를 잘 짝지었어요.';
  if (score >= 50) return '기호와 숫자를 대부분 잘 짝지었어요.';
  if (score >= 40) return '기호와 숫자를 차근차근 짝지었어요.';
  return '기호와 숫자를 짝짓는 연습을 마쳤어요.';
}

// 결과: White 배경 위에 핵심 정보를 직접 배치한다.
// 점수를 화면의 Visual Anchor로 세우고, 점수 Count-up → 평가 문구 등장 순서로 짧게 연출한다.
export default function ResultScreen({
  score,
  totalQuestions,
  correctCount,
  onRetry,
  onNext,
}: ResultScreenProps) {
  const [displayed, setDisplayed] = useState(0);
  const [showComment, setShowComment] = useState(false);
  const frameRef = useRef<number>(0);

  const comment = getComment(score);
  const reachedText =
    totalQuestions > 0
      ? `정답 ${correctCount} / ${totalQuestions}`
      : '다시 한 번 도전해볼까요?';

  useEffect(() => {
    const duration = 850;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(score * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setShowComment(true);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [score]);

  return (
    <div className="flex flex-col h-full bg-white px-6 animate-fade-in-up">
      {/* 결과 핵심 콘텐츠: 화면 정중앙보다 약간 상단에 시각적 무게를 둔다 */}
      <div className="flex-1 min-h-0 flex flex-col pt-[9vh]">
        <p className="text-[15px] font-semibold text-foreground-700 text-center">
          기호 맞추기 결과
        </p>

        {/* 점수 Hero Element + 단순 Decorative Visual */}
        <div className="relative mt-10 h-[212px] w-full flex items-center justify-center">
          <div className="pointer-events-none absolute w-[210px] h-[210px] rounded-full bg-primary-50" />
          <div className="pointer-events-none absolute w-[188px] h-[188px] rounded-full border border-primary-100" />
          <div className="pointer-events-none absolute w-[188px] h-[188px] rounded-full border-2 border-transparent border-t-primary-300 rotate-[35deg]" />

          <p className="relative text-[76px] font-bold text-primary-600 leading-none tabular-nums">
            {displayed}
            <span className="text-2xl font-semibold text-primary-500 ml-1.5">점</span>
          </p>
        </div>

        {/* 결과 문구 (2차 정보) */}
        <p
          className={`
            text-[17px] font-semibold text-foreground-900 leading-snug text-center mt-10
            transition-all duration-500
            ${showComment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
          `}
        >
          {comment}
        </p>

        {/* 보조 문구 (결과 문구보다 약하게) */}
        <p
          className={`
            text-[14px] text-foreground-600 leading-snug text-center mt-2.5
            transition-all duration-500
            ${showComment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
          `}
        >
          {reachedText}
        </p>
      </div>

      {/* 하단 CTA — 다시하기 / 다음 (다음이 Primary) */}
      <div className="flex gap-3 pb-[calc(20px+env(safe-area-inset-bottom))]">
        <button
          onClick={onRetry}
          className="flex-1 h-14 rounded-xl bg-white border border-primary-200 text-primary-600 font-semibold text-base whitespace-nowrap
            active:scale-[0.99] transition-transform duration-150 hover:bg-primary-50 cursor-pointer"
        >
          다시하기
        </button>
        <button
          onClick={onNext}
          className="flex-1 h-14 rounded-xl bg-primary-600 text-white font-semibold text-base whitespace-nowrap
            active:scale-[0.99] transition-transform duration-150 hover:bg-primary-700 cursor-pointer"
        >
          다음
        </button>
      </div>
    </div>
  );
}